import { useEffect, useState } from "react";
import React from "react";
import toast from "../../../../utils/toast";
import {
  getPaymentInstrumentData,
  getPaymentInstrumentById,
  addUpdatePaymentInstrument,
  togglePaymentInstrumentStatus,
  deletePaymentInstrumentById,
} from "../../../../services/admin/adminServices";
import { getPaymentModeList } from "../../../../services/admin/adminServices";

export default function PaymentInstruments() {
  const [instrumentsList, setInstrumentsList] = useState([]);
  const [modesList, setModesList] = useState([]);
  const [listLoading, setListLoading] = useState(true);

  // Add Modal
  const [isModalOpenAdd, setIsModalOpenAdd] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addForm, setAddForm] = useState({
    modeId: "",
    nickname: "",
    isEnabled: true,
    requireProof: true,
    sortOrder: 0,
    values: {},
    extraFields: [],
  });
  const [selectedModeForAdd, setSelectedModeForAdd] = useState(null);

  // Edit Modal
  const [isModalOpenEdit, setIsModalOpenEdit] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [selectedModeForEdit, setSelectedModeForEdit] = useState(null);

  // Delete Modal
  const [isModalOpenDelete, setIsModalOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Extra Fields
  const FIELD_TYPES = ["text", "number", "textarea", "file", "date", "select"];
  const VALUE_TYPES = [
    "any",
    "numeric",
    "alpha",
    "alphanumeric",
    "email",
    "phone",
    "url",
  ];

  useEffect(() => {
    fetchInstruments();
    fetchModes();
  }, []);

  const fetchInstruments = async () => {
    setListLoading(true);
    try {
      const res = await getPaymentInstrumentData();
      if (res.statusCode === 1) setInstrumentsList(res.data);
    } catch (err) {
      console.log("Failed to fetch instruments");
    } finally {
      setListLoading(false);
    }
  };

  const fetchModes = async () => {
    try {
      const res = await getPaymentModeList();
      if (res.statusCode === 1) setModesList(res.data);
    } catch (err) {
      console.log("Failed to fetch modes");
    }
  };

  // Group instruments by mode
  const groupedByMode = modesList.map((mode) => ({
    ...mode,
    instruments: instrumentsList.filter((i) => i.modeId === mode.modeId),
  }));

  // ── Toggle ──
  const handleToggle = async (instr) => {
    const newValue = !instr.isEnabled;
    setInstrumentsList((prev) =>
      prev.map((i) =>
        i.instrumentId === instr.instrumentId
          ? { ...i, isEnabled: newValue }
          : i,
      ),
    );
    try {
      const res = await togglePaymentInstrumentStatus(
        instr.instrumentId,
        newValue,
      );
      if (res.statusCode === 1) toast.success(res.message);
      else {
        toast.error(res.message);
        setInstrumentsList((prev) =>
          prev.map((i) => (i.instrumentId === instr.instrumentId ? instr : i)),
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
      setInstrumentsList((prev) =>
        prev.map((i) => (i.instrumentId === instr.instrumentId ? instr : i)),
      );
    }
  };

  // ── Delete ──
  const confirmDelete = async () => {
    try {
      const res = await deletePaymentInstrumentById(deleteId);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setIsModalOpenDelete(false);
        setDeleteId(null);
        fetchInstruments();
      } else toast.error(res.message);
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    }
  };

  // ── Add ──
  const openAddModal = (mode) => {
    setSelectedModeForAdd(mode);
    setAddForm({
      modeId: mode.modeId,
      nickname: "",
      isEnabled: true,
      requireProof: true,
      sortOrder: 0,
      values: {},
      extraFields: [],
    });
    setIsModalOpenAdd(true);
  };

  const closeAddModal = () => {
    setIsModalOpenAdd(false);
    setSelectedModeForAdd(null);
    setAddForm({
      modeId: "",
      nickname: "",
      isEnabled: true,
      requireProof: true,
      sortOrder: 0,
      values: {},
      extraFields: [],
    });
  };

  const handleAddValueChange = (key, value) => {
    setAddForm((prev) => ({
      ...prev,
      values: { ...prev.values, [key]: value },
    }));
  };

  const handleCreateInstrument = async () => {
    if (!addForm.nickname) {
      toast.error("Nickname is required.");
      return;
    }
    setAddLoading(true);
    try {
      const payload = {
        instrumentId: 0,
        modeId: addForm.modeId,
        instrumentFor: "admin",
        ownerId: 0,
        nickname: addForm.nickname,
        isEnabled: addForm.isEnabled,
        requireProof: addForm.requireProof,
        sortOrder: Number(addForm.sortOrder),
        values: addForm.values,
        extraFields: addForm.extraFields,
      };
      const res = await addUpdatePaymentInstrument(payload);
      if (res.statusCode === 1) {
        toast.success(res.message || "Instrument added successfully.");
        closeAddModal();
        fetchInstruments();
      } else toast.error(res.message);
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setAddLoading(false);
    }
  };

  // ── Edit ──
  const openEditModal = async (instrumentId) => {
    try {
      const res = await getPaymentInstrumentById(instrumentId);
      if (res.statusCode === 1) {
        const d = res.data;
        setSelectedInstrument(d);
        const mode = modesList.find((m) => m.modeId === d.modeId);
        setSelectedModeForEdit(mode);
        setEditForm({
          modeId: d.modeId,
          nickname: d.nickname || "",
          isEnabled: d.isEnabled,
          requireProof: d.requireProof,
          sortOrder: d.sortOrder || 0,
          values: d.values || {},
          extraFields: d.extraFields || [],
        });
        setIsModalOpenEdit(true);
      }
    } catch (err) {
      toast.error("Failed to load instrument.");
    }
  };

  const closeEditModal = () => {
    setIsModalOpenEdit(false);
    setSelectedInstrument(null);
    setSelectedModeForEdit(null);
    setEditForm({});
  };

  const handleEditValueChange = (key, value) => {
    setEditForm((prev) => ({
      ...prev,
      values: { ...prev.values, [key]: value },
    }));
  };

  const handleUpdateInstrument = async () => {
    if (!editForm.nickname) {
      toast.error("Nickname is required.");
      return;
    }
    setEditLoading(true);
    try {
      const payload = {
        instrumentId: selectedInstrument.instrumentId,
        modeId: editForm.modeId,
        instrumentFor: "admin",
        ownerId: 0,
        nickname: editForm.nickname,
        isEnabled: editForm.isEnabled,
        requireProof: editForm.requireProof,
        sortOrder: Number(editForm.sortOrder),
        values: editForm.values,
        extraFields: editForm.extraFields,
      };
      const res = await addUpdatePaymentInstrument(payload);
      if (res.statusCode === 1) {
        toast.success(res.message || "Instrument updated successfully.");
        closeEditModal();
        fetchInstruments();
      } else toast.error(res.message);
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setEditLoading(false);
    }
  };

  // Extra Fields handlers
  const addExtraField = (mode, isEdit) => {
    const newField = {
      label: "",
      fieldType: "text",
      valueType: "any",
      placeholder: "",
      isRequired: true,
      options: [],
    };
    if (isEdit) {
      setEditForm((prev) => ({
        ...prev,
        extraFields: [...(prev.extraFields || []), newField],
      }));
    } else {
      setAddForm((prev) => ({
        ...prev,
        extraFields: [...(prev.extraFields || []), newField],
      }));
    }
  };

  const updateExtraField = (index, field, value, isEdit) => {
    if (isEdit) {
      setEditForm((prev) => ({
        ...prev,
        extraFields: prev.extraFields.map((f, i) =>
          i === index ? { ...f, [field]: value } : f,
        ),
      }));
    } else {
      setAddForm((prev) => ({
        ...prev,
        extraFields: prev.extraFields.map((f, i) =>
          i === index ? { ...f, [field]: value } : f,
        ),
      }));
    }
  };

  const removeExtraField = (index, isEdit) => {
    if (isEdit) {
      setEditForm((prev) => ({
        ...prev,
        extraFields: prev.extraFields.filter((_, i) => i !== index),
      }));
    } else {
      setAddForm((prev) => ({
        ...prev,
        extraFields: prev.extraFields.filter((_, i) => i !== index),
      }));
    }
  };

  // Render standard fields from mode
  const renderStdFields = (mode, values, onChange) => {
    if (!mode?.fields?.length)
      return (
        <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
          No fields defined for this mode.
        </div>
      );
    return mode.fields.map((field) => {
      const key = field.label.toLowerCase().replace(/\s+/g, "_");
      return (
        <div className="form-group mb12" key={field.fieldId}>
          <label>
            {field.label}
            {field.isRequired && " *"}
          </label>
          <input
            type={field.fieldType === "number" ? "number" : "text"}
            value={values[key] || ""}
            onChange={(e) => onChange(key, e.target.value)}
            placeholder={field.placeholder}
          />
        </div>
      );
    });
  };

  // Render extra fields section
  const renderExtraFields = (extraFields, isEdit) => (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "var(--text-tertiary)",
              textTransform: "uppercase",
              letterSpacing: ".06em",
            }}
          >
            Extra Fields for Payer
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--text-secondary)",
              marginTop: 3,
            }}
          >
            Ask payer to fill these when submitting payment
          </div>
        </div>
        <button
          className="btn btn-xs btn-primary"
          onClick={() => addExtraField(null, isEdit)}
        >
          + Add Field
        </button>
      </div>
      {!extraFields?.length ? (
        <div
          style={{
            color: "var(--text-tertiary)",
            fontSize: 12,
            padding: 16,
            textAlign: "center",
            border: "0.5px dashed var(--border-mid)",
            borderRadius: "var(--radius-md)",
          }}
        >
          No extra fields. Click "+ Add Field" to require additional info from
          payer.
        </div>
      ) : (
        extraFields.map((f, i) => (
          <div
            key={i}
            style={{
              border: "0.5px solid var(--border-light)",
              borderRadius: "var(--radius-md)",
              padding: 12,
              marginBottom: 8,
              background: "var(--bg-secondary)",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 28px",
                gap: 8,
                marginBottom: 8,
              }}
            >
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontSize: 10 }}>Label</label>
                <input
                  type="text"
                  value={f.label}
                  onChange={(e) =>
                    updateExtraField(i, "label", e.target.value, isEdit)
                  }
                  placeholder="e.g. Remitter Name"
                  style={{ fontSize: 12 }}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontSize: 10 }}>Field Type</label>
                <select
                  value={f.fieldType}
                  onChange={(e) =>
                    updateExtraField(i, "fieldType", e.target.value, isEdit)
                  }
                  style={{ fontSize: 12 }}
                >
                  {FIELD_TYPES.map((ft) => (
                    <option key={ft} value={ft}>
                      {ft}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => removeExtraField(i, isEdit)}
                style={{
                  width: 28,
                  height: 28,
                  border: "0.5px solid #F09595",
                  background: "var(--red-light)",
                  color: "var(--red)",
                  borderRadius: 5,
                  cursor: "pointer",
                  fontSize: 15,
                  alignSelf: "flex-end",
                }}
              >
                ×
              </button>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontSize: 10 }}>Value Type</label>
                <select
                  value={f.valueType}
                  onChange={(e) =>
                    updateExtraField(i, "valueType", e.target.value, isEdit)
                  }
                  style={{ fontSize: 12 }}
                >
                  {VALUE_TYPES.map((vt) => (
                    <option key={vt} value={vt}>
                      {vt}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontSize: 10 }}>Placeholder</label>
                <input
                  type="text"
                  value={f.placeholder}
                  onChange={(e) =>
                    updateExtraField(i, "placeholder", e.target.value, isEdit)
                  }
                  placeholder="Hint text"
                  style={{ fontSize: 12 }}
                />
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={f.isRequired}
                  onChange={(e) =>
                    updateExtraField(i, "isRequired", e.target.checked, isEdit)
                  }
                />
                Required field
              </label>
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title">Payment Instruments</div>
            <div className="page-sub">
              System › Admin payment account details shown to property owners
            </div>
          </div>
        </div>

        <div className="content">
          <div className="info-box mb16">
            <strong>Admin Payment Instruments</strong> — these are your actual
            payment account details (bank accounts, UPI IDs, QR codes) shown to
            Property Owners when they submit an offline wallet recharge request.
            You can add multiple accounts per mode.
          </div>

          {listLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="card mb16">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    {Array.from({ length: 3 }).map((_, j) => (
                      <div
                        key={j}
                        style={{
                          height: 14,
                          borderRadius: 6,
                          background:
                            "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                          backgroundSize: "200% 100%",
                          animation: "shimmer 1.4s infinite",
                          width: j === 0 ? "40%" : "70%",
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))
            : groupedByMode.map((mode) => (
                <div key={mode.modeId} className="card mb16">
                  {/* Mode Header */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 14,
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <span style={{ fontSize: 22 }}>{mode.icon}</span>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>
                          {mode.name}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-secondary)",
                          }}
                        >
                          {mode.instruments.length} instrument(s) — shown to
                          property owners for offline recharges
                        </div>
                      </div>
                    </div>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => openAddModal(mode)}
                    >
                      + Add {mode.name}
                    </button>
                  </div>

                  {/* Instruments */}
                  {mode.instruments.length === 0 ? (
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--text-tertiary)",
                        padding: "8px 0",
                      }}
                    >
                      No instruments added yet.
                    </div>
                  ) : (
                    mode.instruments.map((instr) => (
                      <div
                        key={instr.instrumentId}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "14px 1fr auto",
                          gap: 12,
                          alignItems: "start",
                          padding: 14,
                          background: instr.isEnabled
                            ? "var(--bg-primary)"
                            : "var(--bg-secondary)",
                          border: "0.5px solid var(--border-light)",
                          borderRadius: "var(--radius-md)",
                          marginBottom: 8,
                        }}
                      >
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: instr.isEnabled ? "#3B6D11" : "#ccc",
                            marginTop: 3,
                            flexShrink: 0,
                          }}
                        />

                        <div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              marginBottom: 4,
                            }}
                          >
                            {instr.nickname}
                          </div>
                          {/* Values */}
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "130px 1fr",
                              gap: "3px 10px",
                              fontSize: 11,
                            }}
                          >
                            {Object.entries(instr.values || {}).map(
                              ([key, value]) => (
                                <React.Fragment key={key}>
                                  <span
                                    style={{
                                      color: "var(--text-tertiary)",
                                      textTransform: "capitalize",
                                    }}
                                  >
                                    {key.replace(/_/g, " ")}:
                                  </span>
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    {value}
                                  </span>
                                </React.Fragment>
                              ),
                            )}
                          </div>
                          {instr.extraFields?.length > 0 && (
                            <div
                              style={{
                                marginTop: 4,
                                fontSize: 10,
                                color: "var(--teal)",
                              }}
                            >
                              + {instr.extraFields.length} extra payer field(s):{" "}
                              {instr.extraFields.map((f) => f.label).join(", ")}
                            </div>
                          )}
                          {instr.requireProof && (
                            <div style={{ marginTop: 3, fontSize: 10 }}>
                              <span
                                style={{
                                  color: "var(--amber)",
                                  fontWeight: 600,
                                }}
                              >
                                📷 Screenshot required
                              </span>
                            </div>
                          )}
                        </div>

                        <div
                          style={{
                            display: "flex",
                            gap: 5,
                            alignItems: "center",
                            flexShrink: 0,
                          }}
                        >
                          <label className="toggle">
                            <input
                              type="checkbox"
                              checked={instr.isEnabled}
                              onChange={() => handleToggle(instr)}
                            />
                            <span className="toggle-slider" />
                          </label>
                          <button
                            className="btn btn-xs"
                            onClick={() => openEditModal(instr.instrumentId)}
                          >
                            ✎ Edit
                          </button>
                          <button
                            className="btn btn-xs btn-danger"
                            onClick={() => {
                              setDeleteId(instr.instrumentId);
                              setIsModalOpenDelete(true);
                            }}
                          >
                            🗑
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ))}
        </div>
      </div>

      {/* ── Add Modal ── */}
      {isModalOpenAdd && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 720, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">
                Add {selectedModeForAdd?.name} Instrument
              </div>
              <button className="modal-close" onClick={closeAddModal}>
                ✕
              </button>
            </div>
            <div
              className="modal-body"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 20,
              }}
            >
              {/* LEFT */}
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                    letterSpacing: ".06em",
                    marginBottom: 12,
                  }}
                >
                  Instrument Details
                </div>

                <div className="form-group mb12">
                  <label>Nickname *</label>
                  <input
                    type="text"
                    value={addForm.nickname}
                    onChange={(e) =>
                      setAddForm((prev) => ({
                        ...prev,
                        nickname: e.target.value,
                      }))
                    }
                    placeholder="e.g. HDFC Primary Account, Main UPI"
                  />
                  <div className="input-hint">
                    Shown in the instrument selector
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    marginBottom: 14,
                    padding: 12,
                    background: "var(--bg-secondary)",
                    borderRadius: "var(--radius-md)",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      cursor: "pointer",
                    }}
                  >
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={addForm.isEnabled}
                        onChange={(e) =>
                          setAddForm((prev) => ({
                            ...prev,
                            isEnabled: e.target.checked,
                          }))
                        }
                      />
                      <span className="toggle-slider" />
                    </label>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>
                        Active
                      </div>
                      <div
                        style={{ fontSize: 11, color: "var(--text-secondary)" }}
                      >
                        Visible to payers in the payment form
                      </div>
                    </div>
                  </label>
                  <div
                    style={{
                      borderTop: "0.5px solid var(--border-light)",
                      paddingTop: 10,
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        cursor: "pointer",
                      }}
                    >
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={addForm.requireProof}
                          onChange={(e) =>
                            setAddForm((prev) => ({
                              ...prev,
                              requireProof: e.target.checked,
                            }))
                          }
                        />
                        <span className="toggle-slider" />
                      </label>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>
                          Require Payment Proof{" "}
                          <span
                            style={{
                              background: "var(--amber-light)",
                              color: "#92400E",
                              borderRadius: 4,
                              padding: "1px 6px",
                              fontSize: 10,
                              fontWeight: 700,
                            }}
                          >
                            Screenshot
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-secondary)",
                          }}
                        >
                          Payer must upload screenshot/receipt when using this
                          instrument
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Standard fields from mode */}
                {renderStdFields(
                  selectedModeForAdd,
                  addForm.values,
                  handleAddValueChange,
                )}
              </div>

              {/* RIGHT — Extra Fields */}
              <div>{renderExtraFields(addForm.extraFields, false)}</div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeAddModal}
                disabled={addLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateInstrument}
                disabled={addLoading}
              >
                {addLoading ? "Saving..." : "Save Instrument"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {isModalOpenEdit && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 720, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">
                Edit {selectedInstrument?.modeName} Instrument —{" "}
                {selectedInstrument?.nickname}
              </div>
              <button className="modal-close" onClick={closeEditModal}>
                ✕
              </button>
            </div>
            <div
              className="modal-body"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 20,
              }}
            >
              {/* LEFT */}
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                    letterSpacing: ".06em",
                    marginBottom: 12,
                  }}
                >
                  Instrument Details
                </div>

                <div className="form-group mb12">
                  <label>Nickname *</label>
                  <input
                    type="text"
                    value={editForm.nickname}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        nickname: e.target.value,
                      }))
                    }
                    placeholder="e.g. HDFC Primary Account, Main UPI"
                  />
                  <div className="input-hint">
                    Shown in the instrument selector
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    marginBottom: 14,
                    padding: 12,
                    background: "var(--bg-secondary)",
                    borderRadius: "var(--radius-md)",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      cursor: "pointer",
                    }}
                  >
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={editForm.isEnabled}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            isEnabled: e.target.checked,
                          }))
                        }
                      />
                      <span className="toggle-slider" />
                    </label>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>
                        Active
                      </div>
                      <div
                        style={{ fontSize: 11, color: "var(--text-secondary)" }}
                      >
                        Visible to payers in the payment form
                      </div>
                    </div>
                  </label>
                  <div
                    style={{
                      borderTop: "0.5px solid var(--border-light)",
                      paddingTop: 10,
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        cursor: "pointer",
                      }}
                    >
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={editForm.requireProof}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              requireProof: e.target.checked,
                            }))
                          }
                        />
                        <span className="toggle-slider" />
                      </label>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>
                          Require Payment Proof{" "}
                          <span
                            style={{
                              background: "var(--amber-light)",
                              color: "#92400E",
                              borderRadius: 4,
                              padding: "1px 6px",
                              fontSize: 10,
                              fontWeight: 700,
                            }}
                          >
                            Screenshot
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-secondary)",
                          }}
                        >
                          Payer must upload screenshot/receipt when using this
                          instrument
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Standard fields */}
                {renderStdFields(
                  selectedModeForEdit,
                  editForm.values,
                  handleEditValueChange,
                )}
              </div>

              {/* RIGHT — Extra Fields */}
              <div>{renderExtraFields(editForm.extraFields, true)}</div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeEditModal}
                disabled={editLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdateInstrument}
                disabled={editLoading}
              >
                {editLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {isModalOpenDelete && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 400 }}>
            <div className="modal-header">
              <div className="modal-title">Confirm Delete</div>
              <button
                className="modal-close"
                onClick={() => setIsModalOpenDelete(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14, color: "var(--text-primary)" }}>
                Are you sure you want to delete this instrument? This action
                cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={() => setIsModalOpenDelete(false)}
              >
                Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDelete}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
